import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setProgress, setToast } from "../../../redux/slice/system";
import { MemberAPI, RecordAPI } from "../../../axios/api";
import {
  useAbortedEffect,
  useSearch,
  useTable,
  usePop,
} from "../../../components/hooks";

import ModuleBreadCrumb from "../../../components/breadCrumb";
import ModulePopKnowledgeDetail from "./popKnowledge";
import {
  formatOptions,
  setPageSetting,
  formatNumber,
} from "../../../service/common";
import { createRecordExcel } from "./service";
import { system_dropdown, system_table } from "../../../service/system";
import { option_category_obj } from "../../../service/option";

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";

export default function BackMemberList() {
  const route_navigate = useNavigate();
  const dispatch = useDispatch();
  const redux_user = useSelector((state) => state.user);
  const crumbItem = [
    {
      key: 1,
      label: "會員資料管理",
    },
  ];
  const { isPop, popOption, openPop, closePop, renderDialog } = usePop([
    {
      key: "knowledge",
      title: () => "『知識』瀏覽狀況",
      isOpen: false,
    },
  ]);
  const [reload, setReload] = useState({ list: false });
  const {
    setTbData,
    tbTotalRecords,
    setTbTotalRecords,
    setTbFirstIndex,
    setTbRows,
    setTbColumns,
    renderTable,
    setPageAction,
  } = useTable();
  // 篩選條件
  const defaultFilter = {
      page: 1,
      size: system_table.size,
      enabled: null,
    },
    pagePath = "/member";
  const {
    filterData,
    setFilterData,
    search,
    setSearch,
    changeFilter,
    renderSearchWrap,
  } = useSearch({
    defaultFilter,
    pagePath: [pagePath],
    tbSetting: { setTbFirstIndex, setTbRows },
    reloadAction: () => setReload((state) => ({ ...state, list: true })),
  });

  useEffect(() => {
    const action = ({ startIndex, rows }) => {
      let temp = {
        ...search,
        page: Math.floor(startIndex / rows) + 1,
        size: rows,
      };
      setFilterData(temp);
      setSearch(temp);
    };
    setPageAction(
      () =>
        ({ startIndex, rows }) =>
          action({ startIndex, rows })
    );
  }, [search]);

  // 取得列表資料
  useAbortedEffect(
    (signal) => {
      if (!reload.list) return;

      dispatch(setProgress(true));

      setPageSetting({ params: search, page: pagePath });

      let params = {
        ...search,
      };

      MemberAPI.getUserList({ data: params, options: { signal } }).then(
        (result) => {
          let { Response, data, total, message } = result;

          if (Response === 1) {
            setTbData(data);
            setTbTotalRecords(total);
            setReload((state) => ({
              ...state,
              list: false,
            }));
          } else {
            dispatch(
              setToast({
                severity: "error",
                summary: message,
                detail: "",
              })
            );
          }

          dispatch(setProgress(false));
        }
      );
    },
    [reload]
  );

  useEffect(() => {
    setTbColumns([
      {
        field: "name",
        header: "會員姓名",
        className: "flex-grow-1",
        style: { flexBasis: "100px" },
      },
      {
        field: "target",
        header: "設定",
        className: "w-20rem",
        body: (data) => (
          <>
            <Button
              className="p-button-warning p-button-sm mr-2"
              type="button"
              label="基本資料"
              onClick={() =>
                route_navigate(`/back${pagePath}/profile/${data.id}`)
              }
            />
            <Button
              className="p-button-warning p-button-sm mr-2"
              type="button"
              label={option_category_obj.setting.work.label}
              onClick={() =>
                route_navigate(`/back${pagePath}/setting/work/${data.id}`)
              }
            />
            <Button
              className="p-button-warning p-button-sm mr-2"
              type="button"
              label={option_category_obj.setting.dangerous.label}
              onClick={() =>
                route_navigate(`/back${pagePath}/setting/dangerous/${data.id}`)
              }
            />
          </>
        ),
      },
      {
        field: "record",
        header: "記錄",
        className: "w-26rem",
        body: (data) => (
          <>
            <Button
              className="p-button-success p-button-sm mr-2"
              type="button"
              label={option_category_obj.record.heartbeat.label}
              onClick={() =>
                route_navigate(`/back${pagePath}/record/heartbeat/${data.id}`)
              }
            />
            <Button
              className="p-button-success p-button-sm mr-2"
              type="button"
              label={option_category_obj.record.weight.label}
              onClick={() =>
                route_navigate(`/back${pagePath}/record/weight/${data.id}`)
              }
            />
            <Button
              className="p-button-success p-button-sm mr-2"
              type="button"
              label={option_category_obj.record.sugar.label}
              onClick={() =>
                route_navigate(`/back${pagePath}/record/sugar/${data.id}`)
              }
            />
            <Button
              className="p-button-success p-button-sm mr-2"
              type="button"
              label={option_category_obj.record.uncomfortable.label}
              onClick={() =>
                route_navigate(
                  `/back${pagePath}/record/uncomfortable/${data.id}`
                )
              }
            />
            <Button
              className="p-button-success p-button-sm mr-2"
              type="button"
              label={option_category_obj.record.health.label}
              onClick={() =>
                route_navigate(`/back${pagePath}/record/health/${data.id}`)
              }
            />
          </>
        ),
      },
      {
        field: "knowledge",
        header: "瀏覽知識次數",
        className: "w-8rem",
        body: (data) => (
          <>
            <div className="flex align-items-center">
              <Button
                className="p-button-info p-button-sm mr-2"
                type="button"
                icon="pi pi-search"
                onClick={() =>
                  openPop({ type: "knowledge", memberid: data.id })
                }
                disabled={!data.totals}
              />
              {`${data.totals ?? 0} 次`}
            </div>
          </>
        ),
      },
    ]);
  }, [pagePath]);

  const exportAllRecord = async () => {
    dispatch(setProgress(true));

    let result_member = await MemberAPI.getUserList({ data: { page: -1 } }),
      result_record = await RecordAPI.downloadCaseData({
        data: { memberList: [] },
      });

    result_member.data_obj = result_member.data.reduce(
      (pre, cur) => ({ ...pre, [cur.id]: cur }),
      {}
    );

    console.log(result_member);

    let formatData = Object.entries(result_record.data ?? {}).map(
      ([memberid, memberData]) => {
        const {
          member_name = result_member.data_obj[memberid]?.name,
          record_glucose: sugar,
          record_health_behavior: health,
          record_heartbeat_blood_pressure: heartbeat,
          record_uncomfortable_symptoms: uncomfortable,
          record_weight: weight,
        } = memberData;

        return {
          sugar: (
            sugar?.map((item) => ({
              ...item,
              member_name,
            })) ?? []
          ).sort(function (pre, next) {
            return (
              new Date(next.date) - new Date(pre.date) || next.type - pre.type
            );
          }),
          health: (
            health?.map((item) => ({
              ...item,
              score:
                Number(item.q1 ?? 0) +
                Number(item.q2 ?? 0) +
                Number(item.q3 ?? 0) +
                Number(item.q4 ?? 0) +
                Number(item.q5 ?? 0),
              member_name,
            })) ?? []
          ).sort(function (pre, next) {
            return new Date(next.date) - new Date(pre.date);
          }),
          heartbeat: (
            heartbeat?.map((item) => ({ ...item, member_name })) ?? []
          ).sort(function (pre, next) {
            return (
              new Date(next.date) - new Date(pre.date) || next.type - pre.type
            );
          }),
          uncomfortable: (
            uncomfortable?.map((item) => ({ ...item, member_name })) ?? []
          ).sort(function (pre, next) {
            return (
              new Date(next.date) - new Date(pre.date) || next.type - pre.type
            );
          }),
          weight: (
            weight?.map((item) => ({ ...item, member_name })) ?? []
          ).sort(function (pre, next) {
            return (
              new Date(next.date) - new Date(pre.date) || next.type - pre.type
            );
          }),
        };
      }
    );
    createRecordExcel({
      data: formatData,
      finishAction: () => dispatch(setProgress(false)),
    });
  };

  return (
    <>
      <ModuleBreadCrumb crumbItem={crumbItem} />

      <h2 className="my-3 ml-1">會員資料管理</h2>

      <div className="border-round-lg bg-white p-3">
        {renderSearchWrap({
          isShow: true,
          template: (
            <>
              <div className="col-12 md:col-4 xl:col-3 flex align-items-start flex-column mt-2 md:mt-0">
                <label
                  htmlFor="username"
                  className="w-4rem white-space-nowrap text-left mb-1"
                >
                  啟用狀態
                </label>
                <InputText
                  className="w-full"
                  name="name"
                  value={filterData.name}
                  onChange={(e) => changeFilter(e)}
                />
              </div>
              <div
                className={classNames(
                  "col-12 md:col-8 xl:col-9 flex align-items-end mt-2 md:mt-0"
                )}
              >
                <Button
                  className="p-button-info w-auto ml-auto md:ml-0 md:mt-0 px-3"
                  type="button"
                  icon="pi pi-search"
                  label="查詢"
                  onClick={() => {
                    setSearch({
                      ...filterData,
                      page: 1,
                    });
                    setTbFirstIndex(0);
                  }}
                />

                <span className="md:ml-auto ml-2"></span>

                {redux_user.userDetail.type === 9 && (
                  <Button
                    className="p-button-warning w-auto mr-2 px-3"
                    type="button"
                    label="匯出全部記錄"
                    onClick={() => exportAllRecord()}
                  />
                )}
                <Button
                  className="w-auto px-3"
                  type="button"
                  label="新增"
                  onClick={() => route_navigate(`/back${pagePath}/profile/add`)}
                />
              </div>
            </>
          ),
        })}

        {renderTable({
          className: "mt-2",
          options: { scrollable: true, scrollDirection: "both" },
        })}
      </div>

      {renderDialog({
        className: "w-11 lg:w-8 md:w-9 sm:w-10 max-w-30rem",
        children: (
          <>
            {isPop.knowledge?.isOpen && (
              <ModulePopKnowledgeDetail
                prePopOption={popOption}
                closePrePop={closePop}
              />
            )}
          </>
        ),
      })}
    </>
  );
}
